import { clsx } from "@/utils";

/**
 * Decorative "stacked cards" empty-state graphic (Figma 6283:17605 — Assistants empty; identical to
 * the My Clients empty 6338:17180). A back placeholder card + two fanned RM-brand-purple cards + a
 * front "shimmer" card. The purples are the fixed RM brand colours (Vivid Purple #9E77ED @50% /
 * Vivid Purple Dark #6941C6 @20%), NOT the operator `primary` token — this is a RideMinder-branded
 * client-portal graphic, and `primary` renders invisibly for non-purple operator brands (e.g. LGC).
 * Purely decorative; hidden from assistive tech. Reused by the My Clients and Assistants empty states.
 */
export const EmptyStateGraphic = ({ className }: { className?: string }) => (
  <div className={clsx("relative mx-auto h-[80px] w-[164px]", className)} aria-hidden>
    {/* Back placeholder card — peeks below the front card (bottom-0, slightly narrower) */}
    <div className="absolute bottom-0 left-1/2 h-[44px] w-[150.333px] -translate-x-1/2 overflow-hidden rounded-[5.5px] border-[0.917px] border-neutral-mid-gray bg-white">
      <span className="absolute left-[10.08px] top-[8.25px] h-[8.25px] w-[69.667px] rounded-full bg-neutral-gray" />
      <span className="absolute left-[10.08px] top-[25.67px] h-[8.25px] w-[87.083px] rounded-full bg-neutral-gray" />
      <span className="absolute left-[106.33px] top-[25.67px] h-[8.25px] w-[15.583px] rounded-full bg-neutral-gray" />
    </div>

    {/* Two RM-brand-purple cards fanned behind the front card */}
    <div className="absolute left-[calc(50%-13.5px)] top-0 flex size-[73px] -translate-x-1/2 items-center justify-center">
      <div className="rotate-[15deg] -scale-y-100">
        <div className="size-[59.604px] rounded-[7.947px] bg-vivid-purple/50" />
      </div>
    </div>
    <div className="absolute left-[calc(50%+16.64px)] top-[2.67px] flex size-[67.851px] -translate-x-1/2 items-center justify-center">
      <div className="-rotate-[30deg]">
        <div className="size-[49.67px] rounded-[7.947px] bg-vivid-purple-dark/20" />
      </div>
    </div>

    {/* Front white "shimmer" card with three gray placeholder bars */}
    <div className="absolute bottom-[6px] left-1/2 h-[48px] w-[164px] -translate-x-1/2 overflow-hidden rounded-[6px] border border-neutral-mid-gray bg-white shadow-[0px_4px_12px_-4px_rgba(71,84,103,0.1)]">
      <span className="absolute left-[11px] top-[9px] h-[9px] w-[76px] rounded-full bg-neutral-gray" />
      <span className="absolute left-[11px] top-[28px] h-[9px] w-[95px] rounded-full bg-neutral-gray" />
      <span className="absolute left-[116px] top-[28px] h-[9px] w-[17px] rounded-full bg-neutral-gray" />
    </div>
  </div>
);
